import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from 'oidc-client-ts';
import { forgetSignedIn, meansNoSession, rememberSignedIn, restoreSession, signIn, signOut, userManager } from './auth';
import { api, ApiError, type Account, type Pricing } from './api';
import { detectLocale, dictionary, rememberLocale, type Locale } from './i18n';
import { AUTH_PATHS, parsePath, useRoute, type Route } from './routing';
import type { Panel } from './components/Shell';
import { Companies } from './pages/Companies';
import { InvitationScreen } from './pages/Invitation';
import { OfficePanel } from './pages/Office';
import { EmailVerificationNotice } from './pages/EmailVerification';
import { Start, type Path } from './pages/Start';
import { Register, readProgress } from './pages/Register';

/**
 * Token z linku w zaproszeniu. Trzymamy go w pamięci karty, bo logowanie przenosi
 * przeglądarkę do Keycloaka i z powrotem — inaczej po powrocie nie wiedzielibyśmy,
 * co użytkownik chciał przyjąć.
 */
const INVITATION_KEY = 'firmeto-zaproszenie';

function readInvitationToken(): string | null {
  const route = parsePath(window.location.pathname);
  if (route.screen === 'invitation') {
    window.sessionStorage.setItem(INVITATION_KEY, route.token);
    return route.token;
  }
  return window.sessionStorage.getItem(INVITATION_KEY);
}

type Session = { user: User; account: Account } | null;

export function App() {
  const [locale, setLocale] = useState<Locale>(detectLocale);
  const [session, setSession] = useState<Session>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationToken, setInvitationToken] = useState<string | null>(readInvitationToken);
  const [route, go] = useRoute();
  const [path, setPath] = useState<Path>('company');
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const t = useMemo(() => dictionary(locale), [locale]);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const user = await restoreSession();
      if (!user) {
        setSession(null);
        return;
      }
      setSession({ user, account: await api.me() });
    } catch (failure) {
      setSession(null);
      if (failure instanceof ApiError && failure.code !== 'unauthorized') setError(failure.message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    // Powrót z Keycloaka: wymiana kodu na token, a potem czysty adres w pasku przeglądarki
    if (window.location.pathname === AUTH_PATHS.callback) {
      let target: Route = { screen: 'start' };
      userManager
        .signinRedirectCallback()
        .then((user) => {
          rememberSignedIn();
          /*
           * Wracamy tam, skąd się wychodziło.
           *
           * Adres zapamiętany przed przekierowaniem wraca w `state` od Keycloaka. Bez tego
           * każdy link przysłany księgowej prowadziłby po zalogowaniu na listę firm, a nie
           * do dokumentu, o który chodziło — i cała praca nad adresami poszłaby na marne.
           */
          const from = (user.state as { path?: string } | undefined)?.path;
          if (from) target = parsePath(from);
        })
        .catch((failure: unknown) => {
          // Powrót z cichej próby bez sesji to nie awaria, tylko odpowiedź „zaloguj się"
          if (meansNoSession(failure)) forgetSignedIn();
          else setError(failure instanceof Error ? failure.message : String(failure));
        })
        .finally(() => {
          // Adres podmieniamy zawsze — także po odmowie, żeby w pasku nie został kod błędu
          go(target, { replace: true });
          void load();
        });
      return;
    }
    void load();
  }, [load, go]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Cennik jest jawny i nie wymaga konta — ceny na ekranach wejścia biorą się stąd
  useEffect(() => {
    api.pricing().then(setPricing).catch(() => setPricing(null));
  }, []);

  const registering = !invitationToken && route.screen === 'register' && (!session || readProgress() !== null);

  /*
   * Rejestracja przeżywa wyjście do Keycloaka: po powrocie wracamy do formularza, a nie na początek.
   *
   * Tylko raz, przy starcie. Gdyby ta reguła działała przy każdym przejściu na stronę startową,
   * przycisk „wstecz" w formularzu byłby ślepy — odkładał postęp, ale natychmiast wracał do kroku,
   * z którego się wyszło.
   */
  const resumed = useRef(false);
  useEffect(() => {
    if (busy || resumed.current) return;
    resumed.current = true;
    if (route.screen === 'start' && readProgress() !== null) go({ screen: 'register' }, { replace: true });
  }, [busy, route.screen, go]);

  // Zalogowany nie ma po co stać na stronie startowej — jego miejsce to firmy
  useEffect(() => {
    if (busy || !session || invitationToken || registering) return;
    if (route.screen === 'start' || route.screen === 'register') go({ screen: 'companies' }, { replace: true });
  }, [busy, session, invitationToken, registering, route.screen, go]);

  const openCompany = useCallback(
    (companyId: string, panel: Panel) => go({ screen: 'company', companyId, panel }),
    [go],
  );
  const leaveCompany = useCallback(() => go({ screen: 'companies' }), [go]);

  function changeLocale(next: Locale) {
    setLocale(next);
    rememberLocale(next);
  }

  // Przed zalogowaniem ekran jest pełnoekranowy: własna marka, własny wybór języka,
  // bez paska aplikacji — tak jak w projekcie
  if (!busy && (invitationToken || !session || registering)) {
    return (
      <div className="shell">
        <main className="entry">
          {error && <p className="error">{error}</p>}
          {invitationToken ? (
            <InvitationScreen
              token={invitationToken}
              t={t}
              signedIn={session !== null}
              onSignIn={() => void signIn()}
              onAccepted={() => {
                window.sessionStorage.removeItem(INVITATION_KEY);
                setInvitationToken(null);
                go({ screen: 'companies' }, { replace: true });
                void load();
              }}
            />
          ) : registering ? (
            <Register
              t={t}
              locale={locale}
              path={path}
              signedIn={session !== null}
              onLocale={changeLocale}
              onSignIn={(hint) => void signIn(hint)}
              onEnter={() => {
                go({ screen: 'companies' }, { replace: true });
                void load();
              }}
              onBack={() => go({ screen: 'start' }, { replace: true })}
            />
          ) : (
            <Start
              t={t}
              locale={locale}
              path={path}
              pricing={pricing}
              onPath={setPath}
              onLocale={changeLocale}
              onRegister={() => go({ screen: 'register' })}
              onSignIn={() => void signIn()}
            />
          )}
        </main>
      </div>
    );
  }

  /*
   * Po zalogowaniu paska aplikacji już nie ma: marka, wybór języka i wylogowanie przeniosły się
   * do powłoki, która zna kontekst firmy. Tutaj zostaje tylko to, co stoi ponad każdym ekranem —
   * błąd wczytywania i wezwanie do potwierdzenia adresu.
   */
  return (
    <div className="workspace">
      {error && <p className="error">{error}</p>}
      {busy && <p className="muted">{t.loading}</p>}
      {!busy && session && (
        <>
          {!session.account.emailDeliverable && <p className="warning">{t.emailUndeliverable}</p>}
          {/* Bez potwierdzonego adresu nie da się założyć firmy — mówimy to od razu, nie dopiero przy próbie */}
          {!session.account.emailVerified && (
            <EmailVerificationNotice account={session.account} locale={locale} t={t} onVerified={() => void load()} />
          )}
          {route.screen === 'office' ? (
            <OfficePanel
              t={t}
              locale={locale}
              account={session.account}
              onBack={leaveCompany}
              onLocale={changeLocale}
              onSignOut={() => void signOut()}
            />
          ) : (
            <Companies
              t={t}
              locale={locale}
              account={session.account}
              companyId={route.screen === 'company' ? route.companyId : null}
              panel={route.screen === 'company' ? route.panel : 'dashboard'}
              onOpen={openCompany}
              onLeave={leaveCompany}
              onLocale={changeLocale}
              onOffice={() => go({ screen: 'office' })}
              onSignOut={() => void signOut()}
            />
          )}
        </>
      )}
    </div>
  );
}
